import noop from "lodash/noop";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  RecurringSettingsMonthRepeatMode,
  RecurringSettingsSeparator,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { useIntl } from "react-intl";
import { isKeyOfObject } from "utils/types";
import toString from "lodash/toString";
import format from "date-fns/format";
import getDate from "date-fns/getDate";
import getDaysInMonth from "date-fns/getDaysInMonth";
import useFormFieldMeta from "hooks/useFormFieldMeta";
import { getSubfieldErrors, namePathStartsWithMatcher } from "utils/formUtils";
import { NamePath } from "rc-field-form/lib/interface";

import { useWeekDays } from "./partials/RecurringSettingsForm/hooks";
import {
  RecurringSettingsFieldProps,
  UseEventDateWeekdayDataReturnType,
  UseRecurringSettingsFieldReturnType,
} from "./types";

export const useEventDateWeekdayData = (
  eventDate: Date | undefined
): UseEventDateWeekdayDataReturnType =>
  useMemo(() => {
    if (!eventDate) {
      return {};
    }

    const weekOfMonth = Math.ceil(getDate(eventDate) / 7);
    const monthWeeksCount = Math.ceil(getDaysInMonth(eventDate) / 7);

    const isLastWeek = monthWeeksCount === weekOfMonth;

    return {
      weekOfMonth,
      isLastWeek,
    };
  }, [eventDate]);

export const useRecurringSettingsField = ({
  onChange = noop,
}: RecurringSettingsFieldProps): UseRecurringSettingsFieldReturnType => {
  const [showRecurringSettingsModal, setShowRecurringSettingsModal] =
    useState(false);

  return {
    closeModal: useCallback(() => {
      setShowRecurringSettingsModal(false);
    }, []),
    openModal: useCallback(() => {
      setShowRecurringSettingsModal(true);
    }, []),
    onRecurringSettingsFormSubmit: useCallback(
      (values) => {
        setShowRecurringSettingsModal(false);
        onChange(values);
      },
      [onChange]
    ),
    showRecurringSettingsModal,
  };
};

export const useOnEventDateUpdate = (
  { onChange, value }: RecurringSettingsFieldProps,
  eventDate: Date | undefined
) => {
  const { isLastWeek } = useEventDateWeekdayData(eventDate);

  useEffect(() => {
    if (
      !value ||
      value.monthRepeatMode !==
        RecurringSettingsMonthRepeatMode.LAST_WEEK_DAY ||
      isLastWeek ||
      !onChange
    ) {
      return;
    }

    onChange({
      ...value,
      monthRepeatMode: RecurringSettingsMonthRepeatMode.WEEK_DAY,
    });
  }, [isLastWeek, value, onChange]);
};

export const useRecurringSettingsString = (
  value: RecurringSettingsFieldProps["value"],
  eventDate: Date | undefined
): string | undefined => {
  const intl = useIntl();
  const weekDays = useWeekDays();
  const { weekOfMonth } = useEventDateWeekdayData(eventDate);
  const monthDay = format(eventDate || new Date(), "d");
  const weekday = format(eventDate || new Date(), "EEEE");

  return useMemo(() => {
    if (!value) {
      return undefined;
    }

    const { separator, separatorCount, monthRepeatMode } = value;

    switch (separator) {
      case RecurringSettingsSeparator.DAILY: {
        if (!separatorCount) {
          return undefined;
        }

        return intl.formatMessage(
          {
            defaultMessage:
              "{separatorCount, plural, one {Every} other {Every {separatorCount}}} {separatorCount, plural, one {day} other {days}}",
          },
          {
            separatorCount,
          }
        );
      }
      case RecurringSettingsSeparator.WEEKLY: {
        const weekdayNames = weekDays.reduce<string[]>(
          (result, { name, label }) => {
            const nameAsString = (
              Array.isArray(name) ? name.join(".") : name
            )?.toString();

            if (
              !nameAsString ||
              !label ||
              !isKeyOfObject(nameAsString, value) ||
              !value[nameAsString]
            ) {
              return result;
            }

            return [...result, toString(label)];
          },
          []
        );

        return intl.formatMessage(
          {
            defaultMessage:
              "{separatorCount, plural, one {Every} other {Every {separatorCount}}} {separatorCount, plural, one {week} other {weeks}} on {weekDays}",
          },
          {
            separatorCount,
            weekDays: weekdayNames.join(","),
          }
        );
      }
      case RecurringSettingsSeparator.MONTHLY: {
        const monthRepeatString = (() => {
          switch (monthRepeatMode) {
            case RecurringSettingsMonthRepeatMode.WEEK_DAY:
            case RecurringSettingsMonthRepeatMode.LAST_WEEK_DAY: {
              return intl.formatMessage(
                {
                  defaultMessage: "{weekOfMonth} {weekday}",
                },
                {
                  weekday,
                  weekOfMonth:
                    monthRepeatMode ===
                    RecurringSettingsMonthRepeatMode.LAST_WEEK_DAY
                      ? "last"
                      : `${weekOfMonth}.`,
                }
              );
            }
            case RecurringSettingsMonthRepeatMode.MONTH_DAY: {
              return intl.formatMessage(
                {
                  defaultMessage: "{monthDay}. day",
                },
                {
                  monthDay,
                }
              );
            }
            default:
              return undefined;
          }
        })();

        return intl.formatMessage(
          {
            defaultMessage:
              "{separatorCount, plural, one {Every} other {Every {separatorCount}}} {separatorCount, plural, one {month} other {months}} on {monthRepeatString}",
          },
          {
            separatorCount,
            monthRepeatString,
          }
        );
      }
      case RecurringSettingsSeparator.YEARLY: {
        if (!separatorCount) {
          return undefined;
        }

        return intl.formatMessage(
          {
            defaultMessage:
              "{separatorCount, plural, one {Every} other {Every {separatorCount}}} {separatorCount, plural, one {year} other {years}}",
          },
          {
            separatorCount,
          }
        );
      }
      default:
        return undefined;
    }
  }, [intl, monthDay, value, weekDays, weekOfMonth, weekday]);
};

export const useRecurringSettingsFieldErrors = (name: NamePath) => {
  const { fieldErrors } = useFormFieldMeta(namePathStartsWithMatcher(name));

  return useMemo(
    () => getSubfieldErrors(fieldErrors, name),
    [fieldErrors, name]
  );
};
